import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type AssetShareLinkSelectionContext,
  type AssetShareLinkSlotId,
  buildAssetShareLinkOptions,
  getAssetShareLinkOptionByKey,
  getDefaultAssetShareLinkOptionKey,
  resolveSlotOptionKeyFromList,
} from '~/components/SharePrivateClipModal/buildAssetShareLinkSlots';
import { type AssetShareLinkModalClip } from '~/components/SharePrivateClipModal/types';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useListAssetShareLinks } from '~/swr-hooks/shortUrl/useListAssetShareLinks';
import { usePrivateVersionsList } from '~/swr-hooks/versions/usePrivateVersionsList';

type UseAssetShareLinkSlotSelectionParams = {
  /** Pass undefined while clip permissions are still loading. */
  canCreateShareLink?: boolean;
  /** Pass undefined while clip permissions are still loading. */
  canViewAssetVersions?: boolean;
  clip: AssetShareLinkModalClip;
  /**
   * When true, initial selection is the first enabled dropdown option (Current, then Vn→V1).
   * Used when opening share outside asset-details. See `getDefaultAssetShareLinkOptionKey`.
   */
  preferEnabledShareLink?: boolean;
};

export const useAssetShareLinkSlotSelection = ({
  canCreateShareLink,
  canViewAssetVersions,
  clip,
  preferEnabledShareLink = false,
}: UseAssetShareLinkSlotSelectionParams) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const assetId = clip.assetId;
  const selectionContext = useMemo<AssetShareLinkSelectionContext>(
    () => ({
      clipId: clip.id,
      clipVersion: clip.version,
      isDefaultVersion: clip.isDefault,
    }),
    [clip.id, clip.isDefault, clip.version],
  );
  const {
    data: assetShareLinks,
    isFetching: isShareLinksFetching,
    isLoading: isShareLinksLoading,
    isError: isAssetShareLinksListError,
    refetch: refetchAssetShareLinksList,
  } = useListAssetShareLinks({
    workspaceId: currentWorkspace?.id,
    assetId,
  });
  const {
    data: versionsList,
    isFetching: isVersionClipsFetching,
    isLoading: isVersionClipsLoading,
  } = usePrivateVersionsList(
    { assetId, workspaceId: currentWorkspace?.id },
    { enabled: canViewAssetVersions === true && (clip.assetVersionCount ?? 0) > 0 },
  );
  const assetShareLinksItems = useMemo(() => assetShareLinks?.data ?? [], [assetShareLinks?.data]);
  const versionClips = useMemo(() => versionsList?.data.clips ?? [], [versionsList?.data.clips]);
  const options = useMemo(
    () => buildAssetShareLinkOptions(assetShareLinksItems, clip.assetVersionCount, versionClips),
    [assetShareLinksItems, clip.assetVersionCount, versionClips],
  );
  const hasResolvedAssetShareLinksList = assetShareLinks !== undefined;
  const isRetryingAssetShareLinksList = isShareLinksFetching && isAssetShareLinksListError;
  const hasAssetShareLinksListError =
    isAssetShareLinksListError &&
    !isShareLinksLoading &&
    (!hasResolvedAssetShareLinksList || !isShareLinksFetching || isRetryingAssetShareLinksList);
  const isLoading = isShareLinksLoading || isVersionClipsLoading;
  const isFetchingShareLinkOptions = isShareLinksFetching || isVersionClipsFetching;
  const selectionScopeKey = `${assetId}:${clip.id}`;
  const [selectedOptionKey, setSelectedOptionKey] = useState('');
  const hasInitializedSelection = useRef(false);
  const prevSelectionScopeKeyRef = useRef(selectionScopeKey);
  // Survives when an empty option key is replaced by a real short id after create.
  const selectedSlotIdRef = useRef<AssetShareLinkSlotId>('current');

  const canResolveDefaultSelection =
    !isShareLinksLoading &&
    hasResolvedAssetShareLinksList &&
    canCreateShareLink !== undefined &&
    canViewAssetVersions !== undefined &&
    options.length > 0;

  const defaultOptionKey = useMemo(() => {
    if (!canResolveDefaultSelection) {
      return '';
    }

    return getDefaultAssetShareLinkOptionKey({
      context: selectionContext,
      options,
      preferEnabledShareLink,
    });
  }, [canResolveDefaultSelection, options, preferEnabledShareLink, selectionContext]);

  const effectiveOptionKey =
    selectedOptionKey || (!hasInitializedSelection.current && canResolveDefaultSelection ? defaultOptionKey : '');

  const selectedOption = useMemo(
    () => getAssetShareLinkOptionByKey(options, effectiveOptionKey),
    [effectiveOptionKey, options],
  );

  useEffect(() => {
    if (prevSelectionScopeKeyRef.current !== selectionScopeKey) {
      prevSelectionScopeKeyRef.current = selectionScopeKey;
      hasInitializedSelection.current = false;
      selectedSlotIdRef.current = 'current';
      setSelectedOptionKey('');
      return;
    }

    if (hasInitializedSelection.current || !canResolveDefaultSelection || !defaultOptionKey) {
      return;
    }

    // Pin the initial default in state so later option refreshes (e.g. disable toggle)
    // do not re-derive selection from preferEnabledShareLink and jump slots.
    hasInitializedSelection.current = true;
    setSelectedOptionKey(defaultOptionKey);

    const initialOption = getAssetShareLinkOptionByKey(options, defaultOptionKey);

    if (initialOption) {
      selectedSlotIdRef.current = initialOption.slotId;
    }
  }, [canResolveDefaultSelection, defaultOptionKey, options, selectionScopeKey]);

  useEffect(() => {
    if (!hasInitializedSelection.current) {
      return;
    }

    const optionForSelectedKey = getAssetShareLinkOptionByKey(options, selectedOptionKey);

    if (optionForSelectedKey) {
      selectedSlotIdRef.current = optionForSelectedKey.slotId;
      return;
    }

    if (!selectedOptionKey || options.length === 0) {
      return;
    }

    const resolvedOptionKey = resolveSlotOptionKeyFromList(options, selectedSlotIdRef.current);

    if (resolvedOptionKey && resolvedOptionKey !== selectedOptionKey) {
      setSelectedOptionKey(resolvedOptionKey);
    }
  }, [options, selectedOptionKey]);

  const onSelectedOptionChange = useCallback((optionKey: string) => {
    hasInitializedSelection.current = true;
    setSelectedOptionKey(optionKey);
  }, []);

  const retryAssetShareLinksList = useCallback(() => {
    void refetchAssetShareLinksList();
  }, [refetchAssetShareLinksList]);

  return {
    assetShareLinksItems,
    hasAssetShareLinksListError,
    hasResolvedAssetShareLinksList,
    isFetchingShareLinkOptions,
    isLoading,
    isRetryingAssetShareLinksList,
    onSelectedOptionChange,
    options,
    retryAssetShareLinksList,
    selectedOption,
    selectionContext,
  };
};
